"use client";

import React, { useMemo, useState } from "react";
import { ArrowRight, Calculator, Lightbulb, Search, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Link } from "@/components/common/Link";
interface GlossaryTerm {
  id: string;
  letter: string;
  term: string;
  category: string;
  definition: string;
  strategyNote: string;
}
const GLOSSARY_TERMS: GlossaryTerm[] = [{
  id: "adr",
  letter: "A",
  term: "Average Daily Rate (ADR)",
  category: "Performance Metric",
  definition: "A core hospitality metric measuring the average rental revenue earned per paid occupied room in a given period. It evaluates pricing power without reflecting total room inventory capacity.",
  strategyNote: "Room Revenue / Paid Occupied Rooms"
}, {
  id: "crs",
  letter: "C",
  term: "Central Reservation System (CRS)",
  category: "Hospitality Tech",
  definition: "A computerized reservation system that manages room inventory, guest information, and pricing across all direct and indirect sales channels.",
  strategyNote: "Single source of truth for global reservations"
}, {
  id: "cm",
  letter: "C",
  term: "Channel Manager (CM)",
  category: "Hospitality Tech",
  definition: "Software that centrally connects a hotel's Property Management System to various online distribution channels, synchronizing room rates and live availability in real time.",
  strategyNote: "Prevents overbooking & updates all OTAs instantly"
}, {
  id: "rate-shopper",
  letter: "C",
  term: "Compset Rate Shopping (Rate Shopper)",
  category: "Market Intelligence",
  definition: "The automated intelligence process of tracking, comparing, and analyzing room prices across a hotel's defined competitive set to maintain competitive positioning.",
  strategyNote: "Monitors rival rates across OTAs and brand sites"
}, {
  id: "forecasting",
  letter: "D",
  term: "Demand Forecasting (Forecasting)",
  category: "Revenue Strategy",
  definition: "The practice of using historical data, local events, pick-up trends, and market indicators to project future guest room demand and set strategic price tiers.",
  strategyNote: "Drives proactive pricing and staff allocation"
}, {
  id: "direct-booking",
  letter: "D",
  term: "Direct Booking (Direct Channel)",
  category: "Distribution",
  definition: "Reservations made straight through a hotel's own website, phone, or front desk without third-party intermediary commissions.",
  strategyNote: "Eliminates OTA commissions (15% to 25% savings)"
}, {
  id: "dynamic-pricing",
  letter: "D",
  term: "Dynamic Pricing (Flex Pricing)",
  category: "Revenue Strategy",
  definition: "A flexible pricing model where room rates fluctuate continuously based on real-time market demand, remaining lead time, and booking pace.",
  strategyNote: "Maximizes revenue capture at peak demand"
}, {
  id: "goppar",
  letter: "G",
  term: "Gross Operating Profit Per Available Room (GOPPAR)",
  category: "Performance Metric",
  definition: "A comprehensive profitability metric that measures operational profit against total available rooms, accounting for both revenues and operational expenses.",
  strategyNote: "Gross Operating Profit / Total Available Rooms"
}, {
  id: "los",
  letter: "L",
  term: "Length of Stay (LOS)",
  category: "Guest Analytics",
  definition: "The total number of consecutive nights a guest occupies a room during a single booking reservation.",
  strategyNote: "Total Room Nights / Total Guest Bookings"
}, {
  id: "mlos",
  letter: "M",
  term: "Minimum Length of Stay (MLOS)",
  category: "Restrictions & Controls",
  definition: "A revenue management restriction requiring guests to book a minimum number of nights to secure a room, typically applied during high-demand festival or holiday dates.",
  strategyNote: "Protects high-demand shoulder dates from short stays"
}, {
  id: "occ",
  letter: "O",
  term: "Occupancy Rate (OCC)",
  category: "Performance Metric",
  definition: "The percentage of available rooms occupied by paying guests over a specific period, reflecting property utilization.",
  strategyNote: "(Occupied Rooms / Total Available Rooms) x 100"
}, {
  id: "ota",
  letter: "O",
  term: "Online Travel Agency (OTA)",
  category: "Distribution",
  definition: "Third-party digital marketplaces such as Booking.com, Expedia, and Agoda where travelers search, compare, and book hotel rooms.",
  strategyNote: "Provides billboard effect and global reach"
}, {
  id: "overbooking",
  letter: "O",
  term: "Overbooking Strategy (Overbooking)",
  category: "Inventory Control",
  definition: "A calculated revenue tactic of accepting more room reservations than physical capacity to offset expected cancellations and no-shows.",
  strategyNote: "Mitigates spoilage and achieves 100% true occupancy"
}, {
  id: "pms",
  letter: "P",
  term: "Property Management System (PMS)",
  category: "Hospitality Tech",
  definition: "The central software used to manage daily hotel operations, including front-desk check-ins, housekeeping, billing, and room assignments.",
  strategyNote: "Operational backbone of hotel property data"
}, {
  id: "rate-parity",
  letter: "R",
  term: "Rate Parity (Parity)",
  category: "Distribution",
  definition: "The legal or contractual requirement to maintain identical base room prices across all public distribution channels and the hotel's direct website.",
  strategyNote: "Maintains partner trust and brand credibility"
}, {
  id: "rms",
  letter: "R",
  term: "Revenue Management System (RMS)",
  category: "Hospitality Tech",
  definition: "An algorithmic software application that analyzes real-time market data to recommend optimal daily room rates and inventory controls.",
  strategyNote: "Automates rate adjustments with predictive modeling"
}, {
  id: "revpar",
  letter: "R",
  term: "Revenue Per Available Room (RevPAR)",
  category: "Performance Metric",
  definition: "The gold-standard hospitality metric combining both pricing power and room occupancy into one actionable figure.",
  strategyNote: "ADR x Occupancy Rate (or Total Revenue / Total Available Rooms)"
}, {
  id: "yield-management",
  letter: "Y",
  term: "Yield Management (Yielding)",
  category: "Revenue Strategy",
  definition: "The tactical process of selling the right room to the right guest at the right time for the right price to optimize revenue from perishable inventory.",
  strategyNote: "Balances room supply against dynamic market demand"
}];
export default function RevenueGlossary() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLetter, setSelectedLetter] = useState<string>("ALL");
  const availableLetters = useMemo(() => {
    const letters = Array.from(new Set(GLOSSARY_TERMS.map(item => item.letter.toUpperCase())));
    return letters.sort();
  }, []);
  const filteredTerms = useMemo(() => {
    return GLOSSARY_TERMS.filter(item => {
      const matchesSearch = searchTerm.trim() === "" || item.term.toLowerCase().includes(searchTerm.toLowerCase()) || item.category.toLowerCase().includes(searchTerm.toLowerCase()) || item.definition.toLowerCase().includes(searchTerm.toLowerCase()) || item.strategyNote.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesLetter = selectedLetter === "ALL" || item.letter.toUpperCase() === selectedLetter.toUpperCase();
      return matchesSearch && matchesLetter;
    });
  }, [searchTerm, selectedLetter]);
  const groupedTerms = useMemo(() => {
    const groups: Record<string, GlossaryTerm[]> = {};
    filteredTerms.forEach(item => {
      const letter = item.letter.toUpperCase();
      if (!groups[letter]) {
        groups[letter] = [];
      }
      groups[letter].push(item);
    });
    return groups;
  }, [filteredTerms]);
  const sortedGroupKeys = useMemo(() => {
    return Object.keys(groupedTerms).sort();
  }, [groupedTerms]);
  return <section id="revenueglossary" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <Badge variant="outline" className="mb-4 px-3.5 py-1 text-xs font-semibold uppercase tracking-wider border-secondary/40 text-secondary-foreground bg-secondary/10 inline-flex items-center gap-1.5">
            <Sparkles className="size-3.5 text-secondary" />
            Hospitality Intelligence Hub
          </Badge>

          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-4">
            Hotel revenue management glossary
          </h2>

          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Essential hospitality terminology, key commercial performance formulas, and strategic definitions explained for hoteliers and property leaders.
          </p>
        </div>

        {/* Search & Filter Toolbar */}
        <div className="max-w-4xl mx-auto mb-12 space-y-4">
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4.5 text-muted-foreground pointer-events-none" />
            <Input type="search" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search by term, acronym (e.g. RevPAR, ADR, OTA), or keyword..." className="pl-10 pr-4 h-12 text-sm sm:text-base bg-card border-border shadow-xs focus-visible:ring-secondary/50 rounded-lg" />
          </div>

          {/* A-Z Letter Filter */}
          <div className="flex flex-wrap items-center justify-center gap-1.5 pt-1">
            <Button variant={selectedLetter === "ALL" ? "default" : "outline"} size="sm" onClick={() => setSelectedLetter("ALL")} className={`h-8 px-3 text-xs font-semibold uppercase rounded-md transition-colors ${selectedLetter === "ALL" ? "bg-primary text-primary-foreground" : "hover:bg-muted text-muted-foreground hover:text-foreground"}`}>
              All
            </Button>
            {availableLetters.map((letter, i) => {
            const isSelected = selectedLetter === letter;
            return <Button key={letter} data-index={i} variant={isSelected ? "default" : "outline"} size="sm" onClick={() => setSelectedLetter(letter)} className={`size-8 p-0 text-xs font-semibold uppercase rounded-md transition-colors ${isSelected ? "bg-secondary text-secondary-foreground" : "hover:bg-muted text-muted-foreground hover:text-foreground"}`}>
                  {letter}
                </Button>;
          })}
          </div>
        </div>

        {/* Glossary Results List */}
        {sortedGroupKeys.length === 0 ? <div className="text-center py-16 px-4 bg-card border border-dashed border-border rounded-xl">
            <p className="text-base text-muted-foreground font-medium mb-3">
              No matching terminology found for your query.
            </p>
            <Button variant="outline" size="sm" onClick={() => {
          setSearchTerm("");
          setSelectedLetter("ALL");
        }} className="text-xs">
              Reset Filters
            </Button>
          </div> : <div className="space-y-10">
            {sortedGroupKeys.map((letterGroup, groupIndex) => <div key={letterGroup} data-index={groupIndex} className="space-y-4">
                {/* Letter Header Anchor */}
                <div className="flex items-center gap-3 border-b border-border pb-2">
                  <span className="flex items-center justify-center size-8 rounded-md bg-secondary text-secondary-foreground font-serif font-bold text-lg shadow-xs">
                    {letterGroup}
                  </span>
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                    {groupedTerms[letterGroup].length} {groupedTerms[letterGroup].length === 1 ? "Term" : "Terms"}
                  </span>
                </div>

                {/* Grid of Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {groupedTerms[letterGroup].map((item, itemIndex) => <Card key={item.id} data-index={itemIndex} className="flex flex-col justify-between bg-card text-card-foreground border border-border hover:border-secondary/50 shadow-xs hover:shadow-md transition-all rounded-xl">
                      <CardHeader className="space-y-2 pb-3">
                        <div className="flex items-start justify-between gap-2">
                          <Badge variant="secondary" className="bg-secondary/15 text-secondary-foreground border border-secondary/30 font-medium text-[11px] px-2 py-0.5">
                            {item.category}
                          </Badge>
                        </div>
                        <CardTitle className="font-serif text-lg md:text-xl font-bold leading-snug text-foreground">
                          {item.term}
                        </CardTitle>
                      </CardHeader>

                      <CardContent className="space-y-4 pt-0">
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {item.definition}
                        </p>

                        <div className="rounded-lg bg-muted/60 border border-border/80 p-3 text-xs flex items-start gap-2.5">
                          <Calculator className="size-4 text-secondary shrink-0 mt-0.5" />
                          <div className="space-y-0.5">
                            <span className="font-semibold text-foreground block">
                              Strategy &amp; Impact
                            </span>
                            <span className="text-muted-foreground font-mono text-[11px] leading-tight block">
                              {item.strategyNote}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>)}
                </div>
              </div>)}
          </div>}

        {/* Closing CTA */}
        <div className="mt-16 sm:mt-20">
          <div className="bg-primary text-primary-foreground rounded-2xl p-8 sm:p-10 md:p-12 shadow-xl relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="space-y-2 text-center md:text-left z-10 max-w-xl">
              <div className="inline-flex items-center gap-2 text-secondary text-xs font-semibold uppercase tracking-wider">
                <Lightbulb className="size-4 text-secondary" />
                Strategic Execution
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl font-bold leading-tight">
                Need to implement these metrics for your hotel?
              </h3>
              <p className="text-primary-foreground/80 text-sm sm:text-base leading-relaxed">
                Connect directly with Sunil Singh to build custom pricing algorithms, OTA channel strategy, and technology workflows designed for your asset.
              </p>
            </div>

            <div className="z-10 shrink-0">
              <Button asChild size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 font-semibold px-6 h-12 text-sm sm:text-base shadow-md group">
                <Link to="#" className="inline-flex items-center gap-2">
                  Book a Consultation
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}